import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import '../styles/ActionReportSummary.css';

// ── Label maps ────────────────────────────────────────────────────────────────

const CERT_STATUS_LABELS = {
  'rc-full-time-service':          'Recommend Continuance – Full-Time Service',
  'rc-part-time-service':          'Recommend Continuance – Part-Time Service',
  'recommend-continued-licensing': 'Recommend Continued Licensing',
  'recommend-first-licensing':     'Recommend First Licensing',
  'grant-certification':           'Grant Certification',
  'continue-certification':        'Continue Certification',
  'full-time-service':             'Full-Time Service',
  'part-time-service':             'Part-Time Service',
  'discontinue-candidacy':         'Discontinue Candidacy',
  'attend-otm':                    'Attend OTM',
  'recommend-provisional-elder':   'Recommend Provisional Elder',
  'recommend-provisional-deacon':  'Recommend Provisional Deacon',
  'recommend-associate-membership':'Recommend Associate Membership',
  'supply-pastor-cert-status':     'Supply Pastor',
  'supply-pastor-first-year':      'Supply Pastor – First Year Supply',
  'supply-pastor-out-of-compliance':'Supply Pastor – More Than 1 Year (Out of Compliance)',
};

const EDU_LABELS = {
  'complete-course-of-study': 'Complete Course of Study',
  'complete-mdiv-degree':     'Complete M.Div. Degree',
  'complete-bachelor-degree': "Complete Bachelor's Degree",
  'submit-transcripts':       'Submit Transcripts',
};

const DOC_LABELS = {
  'submit-background-check':      'Submit Background Check',
  'submit-psychological-report':  'Submit Psychological Report',
  'submit-credit-check':          'Submit Credit Check',
};

const FOLLOWUP_LABELS = {
  'schedule-next-interview': 'Schedule Next Interview',
  'attend-retreat':          'Attend Retreat',
  'submit-progress-report':  'Submit Progress Report',
};

const STATUS_LABELS = {
  approved:  'Approved',
  denied:    'Denied',
  pending:   'Pending',
  completed: 'Completed',
  deferred:  'Deferred',
};

const formatKey = (key) =>
  key.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const labelList = (items, map) => {
  if (!items || items.length === 0) return '';
  return items.map((k) => map[k] || formatKey(k)).join('\n');
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatVote = (vote) => {
  if (!vote) return '';
  return `Y: ${vote.yes ?? 0}  N: ${vote.no ?? 0}  A: ${vote.abstain ?? 0}`;
};

const escapeCSV = (val) => {
  if (val == null) return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

const downloadCSV = (rows, filename) => {
  const headers = [
    'Candidate Name', 'District', 'Report Date',
    'Action Items', 'Educational Requirements',
    'Documentation & Paperwork', 'Follow-up Actions',
    'Vote Count', 'Notes', 'Next Steps', 'Status',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.candidateName, r.district, r.date,
        r.actionItems, r.educationalRequirements,
        r.documentation, r.followUp,
        r.voteCount, r.notes, r.nextSteps, r.statusLabel,
      ]
        .map(escapeCSV)
        .join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Component ─────────────────────────────────────────────────────────────────

const ActionReportSummary = () => {
  const { districts } = useAuth();

  const [allReports, setAllReports]         = useState([]);
  const [candidateMap, setCandidateMap]     = useState({});
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);

  // Filters
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const firstOfYear = `${now.getFullYear()}-01-01`;
  const [startDate, setStartDate]           = useState(firstOfYear);
  const [endDate, setEndDate]               = useState(today);
  const [districtFilter, setDistrictFilter] = useState('all');
  const [statusFilter, setStatusFilter]     = useState('all');

  // ── Data fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all candidates for the id→district lookup
        const candSnap = await getDocs(collection(db, 'candidates'));
        const map = {};
        candSnap.forEach((d) => {
          const { district } = d.data();
          map[d.id] = district || '';
        });
        setCandidateMap(map);

        // Fetch action reports ordered by date
        const reportsSnap = await getDocs(
          query(collection(db, 'actionReports'), orderBy('date', 'desc'))
        );
        setAllReports(reportsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching action report summary:', err);
        setError('Failed to load report data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ── Derived rows ─────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    return allReports
      .filter((r) => {
        if (startDate && r.date < startDate) return false;
        if (endDate   && r.date > endDate)   return false;
        const district = candidateMap[r.candidateId] || '';
        if (districtFilter !== 'all' && district !== districtFilter) return false;
        if (statusFilter !== 'all' && r.status !== statusFilter) return false;
        return true;
      })
      .map((r) => {
        const ai = r.actionItems || {};
        return {
          id:                    r.id,
          candidateName:         r.candidateName || '',
          district:              candidateMap[r.candidateId] || '—',
          date:                  r.date || '',
          actionItems:           labelList(ai.certificationStatus, CERT_STATUS_LABELS),
          educationalRequirements: labelList(ai.educationalRequirements, EDU_LABELS),
          documentation:         labelList(ai.documentation, DOC_LABELS),
          followUp:              labelList(ai.followUp, FOLLOWUP_LABELS),
          voteCount:             formatVote(r.vote),
          notes:                 r.notes || '',
          nextSteps:             r.nextSteps || '',
          status:                r.status || '',
          statusLabel:           STATUS_LABELS[r.status] || formatKey(r.status || ''),
        };
      });
  }, [allReports, candidateMap, startDate, endDate, districtFilter, statusFilter]);

  // ── Sorted district list for dropdown ────────────────────────────────────
  const districtOptions = useMemo(
    () => [...districts].sort((a, b) => a.name.localeCompare(b.name)),
    [districts]
  );

  const handleExportCSV = () => {
    const period = `${startDate}_to_${endDate}`;
    downloadCSV(rows, `action-report-summary-${period}.csv`);
  };

  const handlePrint = () => window.print();

  // ── Status badge color ───────────────────────────────────────────────────
  const statusClass = (s) => {
    const map = { approved: 'green', denied: 'red', pending: 'yellow', completed: 'blue', deferred: 'orange' };
    return map[s] || 'gray';
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="ars-container">
        <div className="ars-loading">Loading report data…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ars-container">
        <div className="ars-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="ars-container">
      {/* ── Header ── */}
      <div className="ars-header no-print">
        <div className="ars-header-text">
          <h1>Action Report Summary</h1>
          <p>View dCOM action reports by reporting period and district.</p>
        </div>
        <div className="ars-header-actions">
          <button className="ars-btn ars-btn-secondary" onClick={handlePrint}>
            Print
          </button>
          <button className="ars-btn ars-btn-primary" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="ars-filters no-print">
        <div className="ars-filter-group">
          <label>Report Period</label>
          <div className="ars-date-range">
            <input
              type="date"
              value={startDate}
              max={endDate || today}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span className="ars-date-sep">to</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={today}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="ars-filter-group">
          <label>District</label>
          <select value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)}>
            <option value="all">All Districts</option>
            {districtOptions.map((d) => (
              <option key={d.id} value={d.name}>{d.name}</option>
            ))}
          </select>
        </div>

        <div className="ars-filter-group">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Summary bar ── */}
      <div className="ars-summary-bar no-print">
        <span className="ars-count">
          <strong>{rows.length}</strong> report{rows.length !== 1 ? 's' : ''}
          {districtFilter !== 'all' ? ` · ${districtFilter}` : ''}
          {startDate && endDate ? ` · ${startDate} – ${endDate}` : ''}
        </span>
        <div className="ars-status-counts">
          {Object.entries(STATUS_LABELS).map(([val, label]) => {
            const n = rows.filter((r) => r.status === val).length;
            if (n === 0) return null;
            return (
              <span key={val} className={`ars-status-pill ars-status-${statusClass(val)}`}>
                {label}: {n}
              </span>
            );
          })}
        </div>
      </div>

      {/* ── Print header (only visible when printing) ── */}
      <div className="print-only ars-print-header">
        <h2>Action Report Summary</h2>
        <p>
          Period: {startDate || '—'} to {endDate || '—'}
          {districtFilter !== 'all' ? `  ·  District: ${districtFilter}` : '  ·  All Districts'}
          {statusFilter !== 'all' ? `  ·  Status: ${STATUS_LABELS[statusFilter]}` : ''}
        </p>
        <p className="ars-print-generated">Generated: {new Date().toLocaleDateString()}</p>
      </div>

      {/* ── Table ── */}
      {rows.length === 0 ? (
        <div className="ars-empty">No action reports match the selected filters.</div>
      ) : (
        <div className="ars-table-wrapper">
          <table className="ars-table">
            <thead>
              <tr>
                <th>Candidate Name</th>
                <th>District</th>
                <th>Date</th>
                <th>Action Items</th>
                <th>Educational Requirements</th>
                <th>Documentation &amp; Paperwork</th>
                <th>Follow-up Actions</th>
                <th>Vote Count</th>
                <th>Notes</th>
                <th>Next Steps</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="ars-name">{r.candidateName}</td>
                  <td>{r.district}</td>
                  <td className="ars-date">{r.date}</td>
                  <td className="ars-items">{r.actionItems || <span className="ars-empty-cell">—</span>}</td>
                  <td className="ars-items">{r.educationalRequirements || <span className="ars-empty-cell">—</span>}</td>
                  <td className="ars-items">{r.documentation || <span className="ars-empty-cell">—</span>}</td>
                  <td className="ars-items">{r.followUp || <span className="ars-empty-cell">—</span>}</td>
                  <td className="ars-vote">{r.voteCount || <span className="ars-empty-cell">—</span>}</td>
                  <td className="ars-notes">{r.notes || <span className="ars-empty-cell">—</span>}</td>
                  <td className="ars-notes">{r.nextSteps || <span className="ars-empty-cell">—</span>}</td>
                  <td>
                    <span className={`ars-badge ars-badge-${statusClass(r.status)}`}>
                      {r.statusLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActionReportSummary;
